/**
 * The exercise library — a clinician screen, kept deliberately.
 *
 * ============================================================================
 *  IT WAS SCAFFOLDING. IT IS NOW A FEATURE — DECISIONS.md 2026-08-21.
 *
 *  Built in Phase 2 as a temporary window, to be deleted once the prescription
 *  builder and patient runner existed. The builder landed with a preview of
 *  its own, and the doctor kept this anyway, for two things the builder
 *  deliberately does not do:
 *
 *    - look at an exercise WITHOUT opening a patient's chart
 *    - watch the raw measurements the exercise emits
 *
 *  The builder's preview is for judging a dose. This is for browsing the
 *  library and seeing what an exercise actually measures.
 *
 *  It still saves nothing, prescribes nothing, and records no result, and it
 *  must not become a shortcut around real prescribing. Its route is still
 *  /clinician/preview; the navigation calls it "Exercise library".
 * ============================================================================
 *
 * Note what this file does NOT contain: any mention of smooth pursuit,
 * saccades, or any other specific exercise. It reads the registry and renders
 * whatever it finds, from the ParamSpec declarations alone. That is the §1
 * architecture working — and it is why adding the second exercise touches this
 * file zero times.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  available,
  validateParams,
  withDefaults,
  INPUT_MODE_LABELS,
  INPUT_MODE_PLACEHOLDER,
  METRIC_LABELS,
  METRIC_UNITS,
  POSITION_LABELS,
  takesNoInput,
  type ExerciseDefinition,
  type InputMode,
  type MetricSample,
  type ParamValues,
  type Position,
  type StopReason,
} from '@pathways/exercises';
import { api, ApiError, type ClinicianSessionInfo } from '../api.js';
import { ExerciseStage, type ExerciseStageHandle } from '../components/ExerciseStage.js';
import { loadPxPerDegree, savePxPerDegree } from '../components/calibration.js';
import { PathPreview } from '../components/PathPreview.js';
// The settings form and its stepper are shared with the prescription builder.
// This screen used to carry private copies of both; they drifted, and a change
// to how a parameter reads had to be made twice. See DECISIONS.md 2026-08-21.
import { ParamForm, Stepper } from '../components/ParamForm.js';
import styles from './ExercisePreview.module.css';

interface EmittedSample extends MetricSample {
  seq: number;
}

export function ExercisePreview() {
  const navigate = useNavigate();

  // --- who can see this ------------------------------------------------------
  // In development this screen is open, because there is no local database to
  // log in against. In a deployed build it requires a clinician session like
  // every other clinician route.
  const isDev = import.meta.env.DEV;

  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<ClinicianSessionInfo>('/auth/me'),
    retry: false,
    enabled: !isDev,
  });

  useEffect(() => {
    if (isDev) return;
    if (session.isError && session.error instanceof ApiError && session.error.status === 401) {
      navigate('/clinician/login', { replace: true });
    }
  }, [isDev, session.isError, session.error, navigate]);

  // --- what we are previewing ------------------------------------------------
  const definitions = useMemo(() => available(), []);
  const [selectedId, setSelectedId] = useState(() => definitions[0]?.id ?? '');
  const definition = definitions.find((d) => d.id === selectedId);

  const [params, setParams] = useState<ParamValues>(() =>
    definitions[0] ? withDefaults(definitions[0]) : {},
  );
  const [position, setPosition] = useState<Position>('seated');
  const [inputMode, setInputMode] = useState<InputMode>('mouse');
  const [pxPerDegree, setPxPerDegree] = useState(loadPxPerDegree);

  // One monitor, one calibration, whichever screen it was set from. This screen
  // used to keep its own unsaved copy and reset to the default every visit.
  useEffect(() => {
    savePxPerDegree(pxPerDegree);
  }, [pxPerDegree]);

  const [running, setRunning] = useState(false);
  const [samples, setSamples] = useState<EmittedSample[]>([]);
  const [lastStop, setLastStop] = useState<StopReason | null>(null);

  const [fullScreen, setFullScreen] = useState(true);

  const stage = useRef<ExerciseStageHandle | null>(null);
  const seqRef = useRef(0);

  // Starting, stopping, full screen, canvas sizing and the spacebar all live
  // in ExerciseStage now — one implementation, shared with the prescription
  // builder's preview so the two cannot drift apart.
  const startRun = useCallback(async () => {
    seqRef.current = 0;
    setSamples([]);
    setLastStop(null);
    await stage.current?.start();
  }, []);

  const stopRun = useCallback((reason: StopReason) => {
    stage.current?.stop(reason);
  }, []);

  // Reset everything when a different exercise is chosen.
  const selectExercise = useCallback(
    (id: string) => {
      const next = definitions.find((d) => d.id === id);
      if (!next) return;
      setSelectedId(id);
      setParams(withDefaults(next));
      setPosition(next.allowedPositions[0] ?? 'seated');
      setInputMode(next.allowedInputModes[0] ?? INPUT_MODE_PLACEHOLDER);
      setRunning(false);
      setSamples([]);
      setLastStop(null);
    },
    [definitions],
  );

  const setParam = useCallback((key: string, value: number | string | boolean) => {
    setParams((current) => ({ ...current, [key]: value }));
  }, []);

  const messages = definition ? validateParams(definition, params) : [];
  const estimate = definition ? definition.estimateDurationSec(params) : 0;

  if (!isDev && session.isPending) return <p className={styles.loading}>Loading…</p>;
  if (!isDev && session.isError) return null;

  if (!definition) {
    return (
      <div className={styles.empty}>
        <h1>No exercises registered</h1>
        <p>
          The registry is empty. Add an exercise in{' '}
          <code>packages/exercises/src/index.ts</code>.
        </p>
      </div>
    );
  }

  // --- metric readout --------------------------------------------------------
  // Grouped by metric so the doctor sees each series separately without this
  // screen knowing which metrics any given exercise emits.
  const byMetric = new Map<string, number[]>();
  for (const sample of samples) {
    const list = byMetric.get(sample.metric) ?? [];
    list.push(sample.value);
    byMetric.set(sample.metric, list);
  }

  // Field-level notes are anchored to their field by ParamForm; only the
  // cross-field ones need saying again down here.
  const crossField = messages.filter((m) => !m.param);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Exercise library</h1>
          <p className={styles.subtitle}>
            Look at any exercise and what it measures. Nothing here is saved or
            prescribed — to give an exercise to a patient, write it into their
            prescription.
          </p>
        </div>
      </header>

      <div className={styles.layout}>
        {/* ---------------- controls ---------------- */}
        <aside className={styles.controls}>
          <label className={styles.field}>
            <span className={styles.label}>Exercise</span>
            <select
              className={styles.input}
              value={selectedId}
              onChange={(e) => selectExercise(e.target.value)}
            >
              {definitions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>

          <p className={styles.description}>{definition.clinicalDescription}</p>

          <div className={styles.divider} />

          {/*
            The shared settings form — the same component the prescription
            builder uses, so a change to how a parameter reads reaches both
            screens at once. It owns the advanced disclosure and anchors
            field-level validation notes itself.
          */}
          <ParamForm
            params={definition.params}
            values={params}
            onChange={setParam}
            messages={messages}
            idPrefix="library"
          />

          <PathPreview definition={definition} params={params} />

          <div className={styles.divider} />

          <label className={styles.field}>
            <span className={styles.label}>Position</span>
            <select
              className={styles.input}
              value={position}
              onChange={(e) => setPosition(e.target.value as Position)}
            >
              {definition.allowedPositions.map((p) => (
                <option key={p} value={p}>
                  {POSITION_LABELS[p]}
                </option>
              ))}
            </select>
          </label>

          {!takesNoInput(definition) && (
            <label className={styles.field}>
              <span className={styles.label}>Input mode</span>
              <select
                className={styles.input}
                value={inputMode}
                onChange={(e) => setInputMode(e.target.value as InputMode)}
              >
                {definition.allowedInputModes.map((m) => (
                  <option key={m} value={m}>
                    {INPUT_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className={styles.field}>
            <span className={styles.label}>
              Screen calibration <em>{pxPerDegree} px/deg</em>
            </span>
            <Stepper
              id="px-per-degree"
              min={15}
              max={80}
              step={1}
              value={pxPerDegree}
              onChange={setPxPerDegree}
            />
            <span className={styles.help}>
              Stands in for Phase 4 calibration. Higher means the same prescribed
              angle draws physically larger.
            </span>
          </label>
        </aside>

        {/* ---------------- stage ---------------- */}
        <main className={styles.stage}>
          <div className={styles.stageBar}>
            <button
              type="button"
              className={styles.primary}
              onClick={() => (running ? stopRun('clinician_stop') : void startRun())}
            >
              {running ? 'Stop' : 'Run'}
            </button>
            <label className={styles.inlineCheck}>
              <input
                type="checkbox"
                checked={fullScreen}
                onChange={(e) => setFullScreen(e.target.checked)}
                disabled={running}
              />
              Full screen
            </label>
            <span className={styles.estimate}>
              Estimated {Math.floor(estimate / 60)}m {estimate % 60}s
            </span>
            {running && <span className={styles.hint}>Spacebar stops instantly</span>}
            {lastStop && !running && (
              <span className={styles.hint}>Stopped — {lastStop.replace(/_/g, ' ')}</span>
            )}
          </div>

          <ExerciseStage
            ref={stage}
            definition={definition}
            params={params}
            position={position}
            inputMode={inputMode}
            pxPerDegree={pxPerDegree}
            fullScreen={fullScreen}
            onRunningChange={setRunning}
            onStopped={setLastStop}
            onSample={(sample) => {
              seqRef.current += 1;
              const seq = seqRef.current;
              setSamples((current) => [...current, { ...sample, seq }]);
            }}
          />

          {crossField.length > 0 && (
            <ul className={styles.messages}>
              {crossField.map((m) => (
                <li
                  key={m.message}
                  className={m.severity === 'blocking' ? styles.blocking : undefined}
                >
                  <strong>{m.severity === 'blocking' ? 'Cannot run:' : 'Note:'}</strong>{' '}
                  {m.message}
                </li>
              ))}
            </ul>
          )}

          <section className={styles.metrics}>
            <h2 className={styles.metricsHeading}>
              Emitted through <code>ctx.emit()</code> — {samples.length} samples
            </h2>
            {/*
              * An exercise that is not watched must not be described as though
              * it were. The reason comes from the definition, so this screen
              * says the right thing for an exercise it has never heard of.
              *
              * The note REPLACES the "what the monitor watches" line and does
              * not replace the table: an unwatched exercise still emits its
              * completion, and this screen exists to show what an exercise
              * actually emits.
              */}
            {definition.decayNotMonitored ? (
              <p className={styles.help}>
                Not watched by the safety monitor (§9 Layer 3).{' '}
                {definition.decayNotMonitored}
              </p>
            ) : null}

            {byMetric.size === 0 ? (
              definition.decayNotMonitored ? null : (
                <p className={styles.help}>
                  Nothing emitted yet. This is the seam the safety monitor
                  (§9 Layer 3) reads; the numbers below are what it watches.
                </p>
              )
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>n</th>
                    <th>Mean</th>
                    <th>First</th>
                    <th>Last</th>
                  </tr>
                </thead>
                <tbody>
                  {[...byMetric.entries()].map(([metric, values]) => {
                    const unit = METRIC_UNITS[metric as keyof typeof METRIC_UNITS] ?? '';
                    const mean = values.reduce((a, b) => a + b, 0) / values.length;
                    return (
                      <tr key={metric}>
                        <td>
                          {METRIC_LABELS[metric as keyof typeof METRIC_LABELS] ?? metric}{' '}
                          <em>{unit}</em>
                        </td>
                        <td>{values.length}</td>
                        <td>{mean.toFixed(2)}</td>
                        <td>{values[0]?.toFixed(2)}</td>
                        <td>{values[values.length - 1]?.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
